import React, { useEffect } from "react";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { format, isSameDay } from "date-fns";
import { ja } from "date-fns/locale";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { DEADLINE_SHORTENING_RATES } from "../constants/priceMaster";
import {
  type AreaCode,
  type DeadlineShortening,
  type ResearchRequestFormData,
} from "../types";
import { type DeliverySchedule } from "../utils/deliverySchedule";
import { RECEPTION_CUTOFF_HOUR } from "../utils/businessDays";
import { FieldLabel, inputSx, optionBoxSx, SectionCard } from "./formLayout";

interface DeliverySectionProps {
  form: UseFormReturn<ResearchRequestFormData>;
  detectedArea: AreaCode | null;
  standardDelivery: string | null;
  delivery: DeliverySchedule | null;
}

const SPECIFY_DATE = "date";

const DELIVERY_TIP =
  "受付の翌営業日から数えます。15時までのご依頼は当日の受付です。土日祝と年末年始（12/29〜1/3）は営業日に数えません。納期短縮は、案件の内容・エリア・業務の状況によりお受けできない場合があります。標準納期が要相談のときは、短縮と日付の指定はできません。";

const deliveryOptions: Array<[string, string]> = [
  ["0", "短縮なし"],
  ["1", "1営業日短縮（+5%）"],
  ["2", "2営業日短縮（+10%）"],
  ["3", "3営業日短縮（+15%）"],
  [SPECIFY_DATE, "日付を指定"],
];

const formatDate = (date: Date): string =>
  format(date, "M月d日（E）", { locale: ja });

/** 15時までと15時以降で納期が変わるときは両方を、変わらないとき（今日が休業日）は1つを出す */
const DeliveryDates: React.FC<{
  dates: DeliverySchedule["standardDates"];
}> = ({ dates }) =>
  isSameDay(dates.beforeCutoff, dates.afterCutoff) ? (
    <Box>納期の目安：{formatDate(dates.beforeCutoff)}（次の営業日の受付）</Box>
  ) : (
    <Box>
      納期の目安：本日{RECEPTION_CUTOFF_HOUR}時までのご依頼は
      {formatDate(dates.beforeCutoff)}、{RECEPTION_CUTOFF_HOUR}時以降は
      {formatDate(dates.afterCutoff)}
    </Box>
  );

const getDateHelperText = (delivery: DeliverySchedule | null): string => {
  if (!delivery) return "";
  if (delivery.quoteRequired) {
    return "3営業日短縮より早い日付のため、納期と金額は個別にお見積りします。";
  }
  if (delivery.shortening > 0) {
    const rate = DEADLINE_SHORTENING_RATES[delivery.shortening] * 100;
    return `この日付には${delivery.shortening}営業日の短縮（+${rate}%）が必要です。`;
  }
  return "";
};

export const DeliverySection: React.FC<DeliverySectionProps> = ({
  form,
  detectedArea,
  standardDelivery,
  delivery,
}) => {
  const { control, register, setValue, formState } = form;
  const specifyDeliveryDate = useWatch({
    control,
    name: "specifyDeliveryDate",
  });
  const deadlineShortening = useWatch({ control, name: "deadlineShortening" });
  const isDeliveryConsultation = standardDelivery === "要相談";

  // 標準納期が要相談のときは、短縮も日付の指定もできない
  useEffect(() => {
    if (!isDeliveryConsultation) return;
    if (specifyDeliveryDate) {
      setValue("specifyDeliveryDate", false, { shouldDirty: true });
      setValue("desiredDeliveryDate", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (deadlineShortening !== 0) {
      setValue("deadlineShortening", 0, { shouldDirty: true });
    }
  }, [
    deadlineShortening,
    isDeliveryConsultation,
    setValue,
    specifyDeliveryDate,
  ]);

  const selectOption = (value: string): void => {
    const specify = value === SPECIFY_DATE;
    setValue("specifyDeliveryDate", specify, { shouldDirty: true });
    setValue(
      "deadlineShortening",
      specify ? 0 : (Number(value) as DeadlineShortening),
      { shouldDirty: true }
    );
    if (!specify) {
      setValue("desiredDeliveryDate", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const dateHelperText =
    formState.errors.desiredDeliveryDate?.message ??
    getDateHelperText(delivery);

  return (
    <SectionCard title="納期" titleTip={DELIVERY_TIP}>
      {detectedArea && standardDelivery ? (
        <Alert
          severity="info"
          data-testid="standard-delivery"
          // 日付を指定したときは、標準の納期を参考の表示にとどめる
          sx={{
            mb: 2,
            borderRadius: "12px",
            opacity: specifyDeliveryDate ? 0.5 : 1,
          }}
        >
          <Box>
            エリア{detectedArea} / {standardDelivery}
            {!specifyDeliveryDate && deadlineShortening > 0
              ? `（${deadlineShortening}営業日短縮）`
              : ""}
          </Box>
          {delivery ? (
            <DeliveryDates
              dates={
                specifyDeliveryDate
                  ? delivery.standardDates
                  : delivery.shortenedDates
              }
            />
          ) : null}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
          物件と依頼種別を入力すると標準納期を表示します。
        </Alert>
      )}
      <FormControl disabled={isDeliveryConsultation} fullWidth>
        <FieldLabel>納期の指定</FieldLabel>
        <RadioGroup
          aria-label="納期の指定"
          value={
            specifyDeliveryDate ? SPECIFY_DATE : String(deadlineShortening)
          }
          onChange={(event) => {
            selectOption(event.target.value);
          }}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: 1,
          }}
        >
          {deliveryOptions.map(([value, label]) => {
            const checked =
              (specifyDeliveryDate
                ? SPECIFY_DATE
                : String(deadlineShortening)) === value;
            return (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={label}
                sx={optionBoxSx(checked)}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
      {specifyDeliveryDate ? (
        <Box sx={{ mt: 2, maxWidth: { md: 320 } }}>
          <FieldLabel required htmlFor="desired-delivery-date">
            希望納期
          </FieldLabel>
          <TextField
            {...register("desiredDeliveryDate")}
            id="desired-delivery-date"
            type="date"
            size="small"
            fullWidth
            inputProps={{
              "aria-label": "希望納期",
              min: format(new Date(), "yyyy-MM-dd"),
            }}
            error={Boolean(formState.errors.desiredDeliveryDate)}
            helperText={dateHelperText}
            sx={inputSx}
          />
        </Box>
      ) : null}
    </SectionCard>
  );
};
